import { jsx as _jsx } from "react/jsx-runtime";
import { useVariableData } from "@ploy-lib/calculation";
import { BeneficialOwnerResource, CustomerInfoSearchResource } from "@ploy-lib/rest-resources";
import { createValidationHelpers } from "@ploy-lib/validation-helpers";
import { useEffect, useState } from "react";
import { memo, useCallback } from "react";
import { useIntl } from "react-intl";
import { useFetcher, useResource } from "@rest-hooks/core";
import { UpdateBeneficialOwnerFieldView } from ".";
import { useAppLoad } from "../../../appLoadContext";
import { useTemplateFieldIsVisible } from "../../../hooks";
import { createBeneficialOwnerConfig, totalShareValidation } from "./BeneficialOwnerFieldConfig";
import { useSearchParams } from "react-router-dom";
const mapCustomerResourceToBeneficialOwner = (customer) => {
    const beneficialOwner = {
        firstname: customer?.cust?.firstname,
        lastname: customer?.cust?.lastname,
        name: customer?.cust?.customerName,
        address: customer?.addr?.address1,
        addressCity: customer?.addr?.addressCity,
        addressZip: customer?.addr?.addressZip
    };
    return beneficialOwner;
};
/**
 * Wrapper for container and view
 */
const UpdateBeneficialOwnerField = memo((props) => {
    const { form, field } = props;
    return _jsx(UpdateBeneficialOwnerFieldContainer, { form: form, field: field });
});
/**
 * Container for UpdateBemeficialOwnerField. Contains logic for fetching and altering of resource
 */
const UpdateBeneficialOwnerFieldContainer = memo((props) => {
    const { form: { setFieldValue }, field } = props;
    const intl = useIntl();
    const setNumberOfBeneficialOwners = useCallback((number) => {
        setFieldValue(props.field.name, number);
    }, [setFieldValue, props.field.name]);
    const updateNumberOfBeneficialowners = useCallback((int) => {
        setNumberOfBeneficialOwners(props.field.value + int);
    }, [setNumberOfBeneficialOwners, props.field.value]);
    const [selectedElement, setSelectedElement] = useState(undefined); // Used for total share validation
    const validation = createValidationHelpers(intl);
    const namespace = "MONEY_LAUNDERING";
    const { value: useAlternativeEntNrValidation } = useVariableData(namespace, "UseAlternativeEntNrValidation");
    const { value: shortDateFormat } = useVariableData(namespace, "ShortDateFormat");
    const { value: disableSsnValidation } = useVariableData(namespace, "DisableSsnValidation");
    const { value: disableShareValidation } = useVariableData(namespace, "DisableShareValidation");
    const validateTotalShares = useVariableData(namespace, "ValidateTotalShares")
        .value === "true";
    let beneficialOwnerConfig = createBeneficialOwnerConfig(intl, validation, useAlternativeEntNrValidation ?? false, shortDateFormat ?? "", disableSsnValidation ?? false, disableShareValidation ?? false);
    const [searchParams, _] = useSearchParams();
    const customerContext = searchParams.get("customerContext");
    const { applicationNumber } = useAppLoad();
    const { value: epId } = useVariableData(namespace, "EpGuid");
    var isFieldVisible = useTemplateFieldIsVisible();
    var includeZip = isFieldVisible({
        name: "IncludeZip",
        namespace: namespace
    });
    var fnameAndLnameForWCService = isFieldVisible({
        name: "FnameAndLnameForWCService",
        namespace: namespace
    });
    const beneficialOwners = useResource(BeneficialOwnerResource.list(), {
        epId,
        includeFirstAndLastname: fnameAndLnameForWCService,
        includeZip,
        applicationNumber,
        customerContext
    });
    if (validateTotalShares) {
        const newShareConfig = totalShareValidation(intl, beneficialOwners.reduce((total, bo) => total + Number(bo.share), 0), selectedElement);
        beneficialOwnerConfig = beneficialOwnerConfig.map(config => config.name !== newShareConfig.name ? config : newShareConfig);
    }
    useEffect(() => {
        setNumberOfBeneficialOwners(beneficialOwners.length);
    }, [beneficialOwners, setNumberOfBeneficialOwners]);
    const updateBeneficialOwner = useFetcher(BeneficialOwnerResource.update());
    const onUpdateBeneficialOwner = (beneficialOwner) => {
        return updateBeneficialOwner({ id: beneficialOwner.id, epId: epId }, { beneficialOwner, customerContext });
    };
    const deleteBeneficialOwner = useFetcher(BeneficialOwnerResource.delete());
    const onDeleteBeneficialOwner = useCallback((beneficialOwner) => {
        updateNumberOfBeneficialowners(-1);
        return deleteBeneficialOwner({
            id: beneficialOwner.id,
            epId: epId,
            customerContext: customerContext
        });
    }, [
        deleteBeneficialOwner,
        epId,
        updateNumberOfBeneficialowners,
        customerContext
    ]);
    const createBeneficialOwner = useFetcher(BeneficialOwnerResource.create());
    const onCreateBeneficialOwner = useCallback((beneficialOwner) => {
        const { id, addDate, addUser, ...rest } = beneficialOwner;
        updateNumberOfBeneficialowners(1);
        return createBeneficialOwner({ epId: epId }, { beneficialOwner: rest, customerContext }, [
            [
                BeneficialOwnerResource.list(),
                {
                    epId,
                    includeFirstAndLastname: fnameAndLnameForWCService,
                    includeZip,
                    applicationNumber,
                    customerContext
                },
                (newBeneficialOwnerId, beneficialOwnerIds) => [...(beneficialOwnerIds || []), newBeneficialOwnerId]
            ]
        ]);
    }, [
        createBeneficialOwner,
        epId,
        fnameAndLnameForWCService,
        includeZip,
        applicationNumber,
        updateNumberOfBeneficialowners,
        customerContext
    ]);
    const onElementSelected = (id) => {
        const owner = beneficialOwners.find(owner => owner.id == id);
        setSelectedElement(owner);
    };
    const searchCustomer = useFetcher(CustomerInfoSearchResource.detail());
    const getPersonalInfoFromSsn = useCallback(async (beneficialOwner) => {
        const customer = await searchCustomer({
            custNo: beneficialOwner.entNr
        });
        const beneficialOwnerNewInfo = mapCustomerResourceToBeneficialOwner(customer);
        const newBeneficialOwner = {
            ...beneficialOwner,
            ...beneficialOwnerNewInfo
        };
        return newBeneficialOwner;
    }, [searchCustomer]);
    return (_jsx(UpdateBeneficialOwnerFieldView, { fieldName: field.name, beneficialOwners: beneficialOwners, beneficialOwnerConfig: beneficialOwnerConfig, onCreateBeneficialOwner: onCreateBeneficialOwner, onUpdateBeneficialOwner: onUpdateBeneficialOwner, onDeleteBeneficialOwner: onDeleteBeneficialOwner, onElementSelected: validateTotalShares ? onElementSelected : undefined, getPersonalInfoFromSsn: getPersonalInfoFromSsn }));
});
UpdateBeneficialOwnerField.displayName = "UpdateBeneficialOwnerField";
UpdateBeneficialOwnerFieldContainer.displayName =
    "UpdateBeneficialOwnerFieldContainer";
export { UpdateBeneficialOwnerField };
//# sourceMappingURL=UpdateBeneficialOwnerFieldContainer.js.map